import _ from 'lodash'
import moment from 'moment'
import { connect } from 'react-redux'
import { Toast, Loader } from '@vzmi/shp_shared_compo'
import { ExcelExport, ExcelExportHelper } from '@vzmi/component_ec_nodejs_util'
import { getFeatureToggle } from '@vzmi/component_ec_nodejs_util/dist/lib/FeatureToggleUtil'
import safeAwait from '../../../../../../lib/safeAwait'
import SPBaseModule from '../../../../../lib/SPBaseModule'
import {
  DATE_FORMAT,
  getAPIFormatDate
} from '../../../../../lib/MomentHelper'
import errorHelper from '../../../../../../lib/errorHelper'
import Block from '../../../../Common/NewDashboard/Block/Block'
import NoData from '../../../../Common/NoData/NoData'
import withConnectedExport from '../../../../../containers/Common/WithConnectedExport'
import SearchForm, {
  FIELD as SEARCH_FIELD,
  CONFIG as SEARCH_CONFIG
} from './SearchForm/SearchForm'
import ViewsChart, {
  INTERNAL_SORT,
  EXTERNAL_SORT
} from './ViewsChart/ViewsChart'
import { LISTING_TYPE } from '../../constants/listing'
import { REFERRAL_TYPE } from '../../constants/referralType'
import { CARRIER } from '../../constants/carrier'
import {
  LISTING_ID_NOT_FOUND,
  NOT_FOUND
} from '../../constants/noData'
import { GA_CLICK_ACTION } from '../PageViewsAnalysis.ga'
import { getDefaultTimezoneMoment } from '../../../../../../lib/renderUtil'
import styles from '../../../DashboardReminder.css'

export const ALL_CARRIERS = [
  CARRIER.MOBILE_WEB,
  CARRIER.DESKTOP_WEB
]

export const getParamsList = formValues => {
  const payloads = []
  const {
    mainRange,
    comparisonRange,
    ...restFormValues
  } = formValues
  const mainPayload = { mainRange, ...restFormValues }
  const comparisonPayload = _.isEmpty(comparisonRange)
    ? {}
    : { comparisonRange, ...restFormValues }

  // 先算主要的瀏覽量來源再算比較的瀏覽量來源
  for (const newFormValues of [mainPayload, comparisonPayload]) {
    if (!_.isEmpty(newFormValues)) {
      const payload = {}

      for (const field of _.keys(newFormValues)) {
        const fieldValue = newFormValues[field]
        switch (field) {
          case SEARCH_FIELD.MAIN_RANGE:
          case SEARCH_FIELD.COMPARISON_RANGE: {
            if (fieldValue) {
              const [
                reportDateStart,
                reportDateEnd
              ] = _.map(fieldValue, getAPIFormatDate)
              _.set(payload, 'reportDateStart', reportDateStart)
              _.set(payload, 'reportDateEnd', reportDateEnd)
            }
            break
          }
          case SEARCH_FIELD.LISTING_TYPE: {
            if (fieldValue === LISTING_TYPE.ORDER) {
              const fieldKey = SEARCH_FIELD.LISTING_ID
              _.set(payload, fieldKey, newFormValues[fieldKey])
            }
            break
          }
          case SEARCH_FIELD.CARRIERS: {
            if (fieldValue === CARRIER.ALL) {
              _.set(payload, field, ALL_CARRIERS)
              break
            }
            _.set(payload, field, fieldValue)
            break
          }
          case SEARCH_FIELD.REFERRAL_TYPE: {
            _.set(payload, field, fieldValue)
            break
          }
          default:
            break
        }
      }

      payloads.push(payload)
    }
  }

  return payloads
}

export const transformResponseListToChartData = (respList, referralType) => {
  const SORT_LIST = referralType === REFERRAL_TYPE.INTERNAL
    ? INTERNAL_SORT
    : EXTERNAL_SORT
  const groupingPageViews = _.flow(
    // [pageViewsPayload]
    respList => _.map(respList, resp => {
      if (resp) {
        return _.get(resp, 'data', [])
      }
      return []
    }),
    // [pageViews]
    pageViewsList => _.map(pageViewsList, pageViews => {
      // 以 referralGroup 分群加總 pageView
      const pageViewSumByGroup = _.reduce(pageViews, (pageViewsGroup, item) => {
        const {
          reportDate,
          pageView,
          sessionCount,
          referralGroup
        } = item
        if (referralGroup in pageViewsGroup) {
          const prevGroupPageView = _.get(pageViewsGroup, `${referralGroup}.pageView`, 0)
          _.set(pageViewsGroup, `${referralGroup}.pageView`, prevGroupPageView + (pageView || sessionCount))
        } else {
          _.set(pageViewsGroup, referralGroup, { reportDate, pageView: pageView || sessionCount, referralGroup })
        }
        return pageViewsGroup
      }, {})
      return pageViewSumByGroup
    }),
    // [{ group1: pageViews, group2: pageViews }]
    pageViewsGroup => {
      const [mainPageViewsGroup, comparisonPageViewsGroup] = pageViewsGroup
      const pageViewsSortByGroup = _.map(SORT_LIST, sort => {
        const {
          pageView: mainPageView,
          reportDate,
          referralGroup
        } = _.get(mainPageViewsGroup, sort, {})
        const { pageView: comparisonPageView } = _.get(comparisonPageViewsGroup, sort, {})
        if (mainPageView) {
          return {
            mainPageView,
            comparisonPageView,
            reportDate,
            referralGroup
          }
        }
        return {}
      })
      return pageViewsSortByGroup
    },
    pageViewsSortByGroup => _.filter(pageViewsSortByGroup, item => !_.isEmpty(item))
  )(respList)

  return groupingPageViews
}

export const getBarsNameFromFormValues = formValues => {
  const rangesValue = [
    _.get(formValues, SEARCH_FIELD.MAIN_RANGE, []),
    _.get(formValues, SEARCH_FIELD.COMPARISON_RANGE, [])
  ]
  const barsName = _.map(rangesValue, ([start, end]) => {
    return `${moment(start).format(DATE_FORMAT.UI)}~${moment(end).format(DATE_FORMAT.UI)}`
  })
  return barsName
}

export const getNoDataDescription = (formValues, chartData) => {
  const listingId = _.toString(_.get(formValues, SEARCH_FIELD.LISTING_ID, ''))
  if (_.isEmpty(chartData)) {
    if (listingId) {
      return LISTING_ID_NOT_FOUND
    }
    return NOT_FOUND
  }
  return ''
}

export const transformToSheetData = (sheetData, listingId) => _.flow(
  data =>
    // data groupping by date & referralGroup
    data.reduce((acc, obj) => {
      const key = `${obj.reportDate}-${obj.referralGroup}`
      if (!acc[key]) {
        // also adding listingId for sheet
        acc[key] = { ...obj, pageView: obj.pageView || obj.sessionCount, listingId }
      } else {
        // summing pageView/sessionCount count which from different carriers
        acc[key].pageView = acc[key].pageView + (obj.pageView || obj.sessionCount)
      }
      return acc
    }, {})
  ,
  _.flatMap
)(sheetData)

class PageViewsSource extends SPBaseModule {
  constructor (props) {
    super(props)
    this.state = {
      chartData: [],
      barsName: [],
      isComparisonChart: false,
      isExternalReferralType: false,
      isExternalSession: false,
      noDataDescription: NOT_FOUND,
      isComparisonCancelled: false
    }
    this.searchFormProps = null
  }

  componentDidMount () {
    // 使用 setTimeout 確保 SearchForm 的 onFormInit 已經完成
    // 可確保 searchFormProps 設置完畢，且表單的初始值已經正確設置
    setTimeout(() => {
      if (this.searchFormProps && this.searchFormProps.handleSubmit) {
        this.searchFormProps.handleSubmit(this.onSearch)()
      }
    }, 0)
  }

  setSearchFormProps = formProps => (this.searchFormProps = formProps)

  onSearch = values => this.loading(async () => {
    const { fetchAllListingPageViews } = this.props
    const { isComparisonCancelled } = this.state
    const { comparisonRange } = values
    const paramsList = getParamsList({ ...values, comparisonRange: isComparisonCancelled ? null : comparisonRange })
    const [reqError, respList] = await safeAwait(
      Promise.all(_.map(paramsList, fetchAllListingPageViews))
    )

    if (reqError) {
      const error = _.get(reqError, 'payload.errorData.error[0]')
      const errorMessage = errorHelper.parseErrorMessage(error)
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }

    const chartData = transformResponseListToChartData(respList, values[SEARCH_FIELD.REFERRAL_TYPE])
    const isComparisonChart = !_.isEmpty(values[SEARCH_FIELD.COMPARISON_RANGE])
    const isExternalReferralType = values[SEARCH_FIELD.REFERRAL_TYPE] === REFERRAL_TYPE.EXTERNAL
    const isExternalSession = values[SEARCH_FIELD.REFERRAL_TYPE] === REFERRAL_TYPE.EXTERNAL_SESSION
    const barsName = getBarsNameFromFormValues(values)
    const noDataDescription = getNoDataDescription(values, chartData)
    this.setState({
      chartData,
      isComparisonChart,
      isExternalReferralType,
      isExternalSession,
      barsName,
      noDataDescription
    })
    this.setIsSuccess(true)
  })

  onCollapseChange = (isOpen, e) => {
    this.setState({
      isComparisonCancelled: !!isOpen
    })
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.SOURCE_COMPARISON_RANGE
    })
  }

  onListingChange = (e, value) => {
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.SOURCE_LISTING_OPTION,
      payload: value
    })
  }

  onCarriersChange = (e, value) => {
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.SOURCE_CARRIERS,
      payload: value
    })
  }

  onReferralTypeChange = (e, value) => {
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.SOURCE_REFERRAL_TYPE,
      payload: value
    })
  }

  exportExcel = async () => {
    const {
      openExportModal,
      closeExportModal
    } = this.props

    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.SOURCE_EXPORT_EXCEL
    })
    openExportModal()

    const { fetchAllListingPageViews, formValues } = this.props
    const { listingId, listingType, referralType } = formValues
    // omit comparisonRange options which not be supported on excelExport
    const paramsList = getParamsList(_.omit(formValues, 'comparisonRange'))

    const [reqError, respList] = await safeAwait(
      Promise.all(_.map(paramsList, fetchAllListingPageViews))
    )

    if (reqError) {
      closeExportModal({ isError: true })
      return
    }

    const data = _.get(respList, '[0].data', [])
    const ExportHelper = new ExcelExportHelper({
      headers: {
        reportDate: '日期',
        referralGroup: '瀏覽量來源',
        pageView: '瀏覽量',
        // replace for sessionCount description when referralType is externalSession
        ...(referralType === REFERRAL_TYPE.EXTERNAL_SESSION ? { pageView: '工作階段' } : {}),
        ...(listingType === 'order' ? { listingId: '賣場編號' } : {})
      },
      fileName: 'pageviewbysource_{{ nowDate.format("YYYYMMDDhhmmss") }}',
      bookType: 'xlsx'
    })
    const option = {
      fileName: ExportHelper.getFileName({
        nowDate: getDefaultTimezoneMoment()
      }),
      sheets: [
        {
          name: ExportHelper.getSheetName(),
          headers: ExportHelper.getSheetHeaders(),
          data: ExportHelper.convertDataByHeaders(transformToSheetData(data, listingId))
        }
      ]
    }
    const ExcelExporter = new ExcelExport(option)
    ExcelExporter.download({ bookType: ExportHelper.getBookType() })
    closeExportModal()
  }

  render () {
    const {
      isLoading,
      chartData,
      barsName,
      noDataDescription,
      isComparisonChart,
      isExternalReferralType,
      isExternalSession
    } = this.state
    const { formValues, flags } = this.props
    const isPVGA = getFeatureToggle(flags, 'sp_PV_GA')
    const referralType = _.get(formValues, 'referralType')
    const disableExportBtn = isLoading
    return (
      <Loader loaded={!isLoading}>
        <Block>
          <SearchForm
            onFormInit={this.setSearchFormProps}
            onSubmit={this.onSearch}
            onCollapseChange={this.onCollapseChange}
            onListingChange={this.onListingChange}
            onCarriersChange={this.onCarriersChange}
            onReferralTypeChange={this.onReferralTypeChange}
            onExportExcel={this.exportExcel}
            disableExportBtn={disableExportBtn}
            isPVGA={isPVGA}
          />
        </Block>
        { isPVGA && (
          <div className={styles.DashboardReminder}>
            <p>* 「站外流量來源」數據自 2026/01/13(含)起，改由 Google Analytics (GA) 的「工作階段」(Sessions)數據呈現。</p>
            { referralType === REFERRAL_TYPE.INTERNAL && (
              <p>* 「站內」流量來源分析數據自 2026/01/13(含)後將不再提供，您仍可檢視並下載2026/01/13 以前的站內瀏覽量數據</p>
            )}
            { referralType === REFERRAL_TYPE.EXTERNAL && (
              <p>* 「站外-瀏覽量」流量來源分析數據自 2026/01/13(含)後將不再提供，您仍可檢視並下載2026/01/13 以前的站外-瀏覽量數據。</p>
            )}
            { referralType === REFERRAL_TYPE.EXTERNAL_SESSION && (
              <p>* 「站外-工作階段」可查詢最早日期為 2025/10/01，可查詢最近的日期為兩天前。</p>
            )}
          </div>
        )}
        <Block
          data-role='chart-area'
          noMarginBottom
        >
          {_.isEmpty(chartData)
            ? (
              <NoData description={noDataDescription} />
            )
            : (
              <ViewsChart
                data={chartData}
                barsName={barsName}
                isComparison={isComparisonChart}
                isExternal={isExternalReferralType}
                isExternalSession={isExternalSession}
              />
            )
          }
        </Block>
      </Loader>
    )
  }
}

const mapStateToProps = state => ({
  formValues: _.get(state, `form.${SEARCH_CONFIG.form}.values`)
})

export default withConnectedExport(connect(mapStateToProps)(PageViewsSource))
